// A row the user added to a list and left empty. rjsf stores an untouched row
// as undefined (null once serialised) and a cleared text input as "", none of
// which is a value: AJV rejects the row ("must be string") and blocks the whole
// save over something the user never meant to submit.
export const isBlankListItem = (item: any) =>
  item === undefined ||
  item === null ||
  (typeof item === 'string' && item.trim() === '');

const SCALAR_ITEM_TYPES = ['string', 'number', 'integer', 'boolean'];
const MAX_DEPTH = 10;

// Follows local $refs (Pydantic v1 `definitions`, v2 `$defs`), the single-entry
// allOf Pydantic v1 wraps a described $ref in, and the anyOf v2 uses for
// Optional[...], down to the node that carries type/items/properties.
const resolve = (root: any, node: any, depth = 0): any => {
  if (!node || typeof node !== 'object' || depth > MAX_DEPTH) return node;
  if (typeof node.$ref === 'string') {
    const match = node.$ref.match(/^#\/(definitions|\$defs)\/(.+)$/);
    const target = match ? root?.[match[1]]?.[match[2]] : undefined;
    return target ? resolve(root, target, depth + 1) : node;
  }
  if (Array.isArray(node.allOf) && node.allOf.length === 1)
    return resolve(root, node.allOf[0], depth + 1);
  if (Array.isArray(node.anyOf) && !node.items && !node.properties) {
    const members = node.anyOf.filter((m: any) => m?.type !== 'null');
    if (members.length === 1) return resolve(root, members[0], depth + 1);
  }
  return node;
};

export const hasScalarListItems = (items: any) => {
  if (!items || typeof items !== 'object' || Array.isArray(items)) return false;
  const types = Array.isArray(items.type) ? items.type : [items.type];
  return (
    types.some((type: any) => SCALAR_ITEM_TYPES.includes(type)) ||
    Array.isArray(items.enum)
  );
};

const clean = (root: any, schemaNode: any, value: any, depth: number): any => {
  const node = resolve(root, schemaNode);
  if (!node || depth > MAX_DEPTH) return value;
  if (Array.isArray(value)) {
    const items = resolve(root, node.items);
    if (hasScalarListItems(items)) {
      const kept = value.filter((item) => !isBlankListItem(item));
      return kept.length === value.length ? value : kept;
    }
    let changed = false;
    const next = value.map((item) => {
      const cleaned = clean(root, items, item, depth + 1);
      if (cleaned !== item) changed = true;
      return cleaned;
    });
    return changed ? next : value;
  }
  if (value && typeof value === 'object' && node.properties) {
    let changed = false;
    const next = { ...value };
    Object.keys(node.properties).forEach((key) => {
      if (!(key in value)) return;
      const cleaned = clean(root, node.properties[key], value[key], depth + 1);
      if (cleaned !== value[key]) {
        next[key] = cleaned;
        changed = true;
      }
    });
    return changed ? next : value;
  }
  return value;
};

// What a save submits: every list of scalars (top-level or nested in an object
// row) without its blank rows. Done on the payload at save time, not in the
// form: dropping a row the moment it is added would undo the Add click. The
// input is never mutated, and an unchanged input comes back as the same object.
export const dropBlankListItems = (schema: any, data: any) =>
  clean(schema, schema, data, 0);
